import React, { useState } from 'react';
import { X, Ruler, CheckCircle2, Sparkles, HelpCircle, ShieldCheck, Heart, ArrowRight } from 'lucide-react';
import { SIZING_PRESETS, SHAPE_OPTIONS } from '../data/products';

interface SizingGuideProps {
  isOpen?: boolean;
  onClose?: () => void;
  isModal?: boolean;
}

export const SizingGuide: React.FC<SizingGuideProps> = ({
  isOpen = true,
  onClose,
  isModal = false,
}) => {
  const [testMm, setTestMm] = useState<number>(15);

  const getRecommendedPreset = (mm: number) => {
    if (mm <= 14) return 'XS';
    if (mm <= 15) return 'S (Most Popular)';
    if (mm <= 16) return 'M';
    return 'L';
  };

  if (isModal && !isOpen) return null;

  const content = (
    <div className="space-y-10">
      
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-2">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FEF08A] text-[#854D0E] text-xs font-bold uppercase tracking-wider border border-[#FDE047]">
          <Ruler className="w-3.5 h-3.5" />
          <span>Interactive Sizing & Care Guide</span>
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl font-extrabold text-[#292524]">
          Find Your Perfect Fit in 60 Seconds
        </h2>
        <p className="text-xs sm:text-sm text-[#78716C]">
          Getting an exact fit ensures your hand-painted press-ons look seamless, feel natural, and stay on through active days!
        </p>
      </div>

      {/* 3-Step Measurement Visual Guide */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        <div className="p-6 rounded-3xl bg-white border border-[#EADFCF] shadow-xs space-y-3 relative overflow-hidden group hover:border-[#D4AF37] transition-colors">
          <div className="w-10 h-10 rounded-2xl bg-[#FEF08A] text-[#854D0E] font-bold text-lg flex items-center justify-center font-serif shadow-xs">
            1
          </div>
          <h3 className="font-serif text-lg font-bold text-[#292524]">Apply Tape</h3>
          <p className="text-xs text-[#78716C] leading-relaxed">
            Place a piece of clear scotch tape horizontally across the widest part of your natural nail bed, pressing firmly into the side grooves.
          </p>
        </div>

        <div className="p-6 rounded-3xl bg-white border border-[#EADFCF] shadow-xs space-y-3 relative overflow-hidden group hover:border-[#D4AF37] transition-colors">
          <div className="w-10 h-10 rounded-2xl bg-[#FCE7F3] text-[#BE185D] font-bold text-lg flex items-center justify-center font-serif shadow-xs">
            2
          </div>
          <h3 className="font-serif text-lg font-bold text-[#292524]">Mark Edges</h3>
          <p className="text-xs text-[#78716C] leading-relaxed">
            Use a fine-tip pen to mark the left and right edges where your nail meets the skin on both sidewalls.
          </p>
        </div>

        <div className="p-6 rounded-3xl bg-white border border-[#EADFCF] shadow-xs space-y-3 relative overflow-hidden group hover:border-[#D4AF37] transition-colors">
          <div className="w-10 h-10 rounded-2xl bg-[#EDE9FE] text-[#6B21A8] font-bold text-lg flex items-center justify-center font-serif shadow-xs">
            3
          </div>
          <h3 className="font-serif text-lg font-bold text-[#292524]">Measure in MM</h3>
          <p className="text-xs text-[#78716C] leading-relaxed">
            Peel the tape off, lay it flat on a ruler measuring in millimeters (mm), and note the exact width of each finger.
          </p>
        </div>

      </div>

      {/* Interactive Size Calculator Tool */}
      <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-[#FEF08A]/40 via-[#FCE7F3]/40 to-[#EDE9FE]/50 border border-[#F3C053]/40 shadow-sm">
        <div className="max-w-2xl mx-auto text-center space-y-4">
          <span className="text-xs font-extrabold uppercase tracking-widest text-[#854D0E]">
            Quick Sizing Preset Tool
          </span>
          <h3 className="font-serif text-2xl font-bold text-[#292524]">
            What is your Thumb width in Millimeters?
          </h3>
          
          <div className="flex items-center justify-center gap-4 pt-2">
            <input
              type="range"
              min="11"
              max="19"
              step="1"
              value={testMm}
              onChange={(e) => setTestMm(Number(e.target.value))}
              className="w-64 accent-[#D4AF37] cursor-pointer"
            />
            <span className="font-serif text-2xl font-extrabold text-[#B45309] bg-white px-3 py-1 rounded-xl shadow-xs border border-[#FDE68A]">
              {testMm} mm
            </span>
          </div>

          <div className="inline-flex items-center gap-2 bg-white px-5 py-2.5 rounded-2xl shadow-sm border border-[#D4AF37]/40 mt-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs sm:text-sm font-semibold text-[#292524]">
              Recommended Preset: <strong className="text-[#B45309]">{getRecommendedPreset(testMm)}</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Standard Size Preset Reference Table */}
      <div className="rounded-3xl bg-white border border-[#EADFCF] p-6 shadow-xs space-y-4">
        <h3 className="font-serif text-xl font-bold text-[#292524] flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#D4AF37]" />
          <span>Preset Size Reference Table</span>
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-[#EADFCF] text-[#854D0E] font-bold">
                <th className="pb-3 px-2">Size</th>
                <th className="pb-3 px-2">Description</th>
                <th className="pb-3 px-2">Nail Numbers (Thumb → Pinky)</th>
                <th className="pb-3 px-2">Millimeter Widths</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5EBE0]">
              {SIZING_PRESETS.map((preset, idx) => (
                <tr key={idx} className="hover:bg-[#FAF5EE]/70 transition-colors">
                  <td className="py-3 px-2 font-bold text-[#292524]">{preset.name}</td>
                  <td className="py-3 px-2 text-[#78716C]">{preset.description}</td>
                  <td className="py-3 px-2 font-mono text-xs text-[#B45309]">{preset.sizes}</td>
                  <td className="py-3 px-2 text-[#57534E]">{preset.mm}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-[#78716C] italic">
          *Have unique sizing or between sizes? Choose "Custom" on any product and enter your 5 exact measurements at no extra charge!
        </p>
      </div>

      {/* How to Apply & Remove (Tabs vs Glue) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Sticky Tabs Card */}
        <div className="p-6 rounded-3xl bg-white border border-[#F472B6]/40 shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-[#BE185D] bg-[#FCE7F3] px-3 py-1 rounded-full">
              Weekend / Meet Wear (2–4 Days)
            </span>
            <Sparkles className="w-4 h-4 text-[#F472B6]" />
          </div>
          <h4 className="font-serif text-lg font-bold text-[#292524]">
            Using Adhesive Sticky Tabs
          </h4>
          <ul className="text-xs text-[#57534E] space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
              <span>Perfect for gymnasts, dancers & weekends before school/practice</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
              <span>Zero damage to natural nail beds</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
              <span>Instant peel removal with no soaking required</span>
            </li>
          </ul>
        </div>

        {/* Liquid Glue Card */}
        <div className="p-6 rounded-3xl bg-white border border-[#D4AF37]/40 shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-[#854D0E] bg-[#FEF08A] px-3 py-1 rounded-full">
              Long-Lasting Wear (2–3+ Weeks)
            </span>
            <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
          </div>
          <h4 className="font-serif text-lg font-bold text-[#292524]">
            Using Salon Liquid Glue
          </h4>
          <ul className="text-xs text-[#57534E] space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
              <span>Buff natural nail shine and wipe with alcohol pad before applying</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
              <span>Apply 1 drop to natural nail + 1 drop to press-on, hold firm for 30s</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
              <span>To remove: Soak in warm soapy water with oil for 10-15 minutes</span>
            </li>
          </ul>
        </div>

      </div>

    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200">
        <div
          className="relative w-full max-w-4xl bg-[#FCFAF8] rounded-3xl shadow-2xl border border-[#EADFCF] p-6 sm:p-10 my-auto max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            aria-label="Close Sizing Guide"
            className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white text-[#78716C] hover:text-[#292524] shadow-md border border-[#E7DEC8] transition-transform hover:scale-105"
          >
            <X className="w-5 h-5" />
          </button>
          {content}
        </div>
      </div>
    );
  }

  return (
    <section id="sizing" className="py-16 md:py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {content}
      </div>
    </section>
  );
};
